import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, shuffle, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { pad: readonly string[]; root: string; stab: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Gm: { pad: ['G3', 'D4', 'Bb4'], root: 'G1', stab: ['G3', 'Bb3', 'D4', 'G4'] },
  Eb: { pad: ['Eb3', 'Bb3', 'G4'], root: 'Eb2', stab: ['Eb3', 'G3', 'Bb3', 'Eb4'] },
  Cm: { pad: ['C3', 'G3', 'Eb4'], root: 'C2', stab: ['C3', 'Eb3', 'G3', 'C4'] },
  D: { pad: ['D3', 'A3', 'F#4'], root: 'D2', stab: ['D3', 'F#3', 'A3', 'D4'] },
  F: { pad: ['F3', 'C4', 'A4'], root: 'F1', stab: ['F3', 'A3', 'C4', 'F4'] },
  Bb: { pad: ['Bb2', 'F3', 'D4'], root: 'Bb1', stab: ['Bb2', 'D3', 'F3', 'Bb3'] },
};

const PROGS = [
  ['Gm', 'Gm', 'Eb', 'D'],
  ['Gm', 'F', 'Eb', 'D'],
  ['Gm', 'Cm', 'Eb', 'D'],
  ['Gm', 'Eb', 'Bb', 'F'],
] as const;

type Groove = { kick: readonly [string, string]; tom: readonly [string, string]; slap: readonly [string, string]; clap: readonly [string, string] };

const GROOVES: readonly Groove[] = [
  {
    kick: ['X..x..x...x..x..', 'X..x..x...x.x...'],
    tom: ['........X.....x.', '........X..x....'],
    slap: ['..o..o.o...o..o.', '..o..o.o..o...oo'],
    clap: ['....X.......X...', '....X.......X..o'],
  },
  {
    kick: ['X..x..x...x..x..', 'X..x..x.x...x...'],
    tom: ['........X..x....', '.........x..X...'],
    slap: ['.o.o.o..o..o.o..', '.o.o.o....o..o.o'],
    clap: ['....X.......X...', '....X..o....X...'],
  },
  {
    kick: ['X..x..x...x..x..', 'X.....x...x..x..'],
    tom: ['...o....X.....x.', '...o....X..x..x.'],
    slap: ['..o..o.....o.o..', '..o..o.o...o..oo'],
    clap: ['....X.......X...', '....X.......X.o.'],
  },
];

const BASS = [
  [0, 0, 0, 0, 0],
  [0, 0, 0, 12, 0],
  [0, 0, 7, 0, 0],
  [0, 12, 0, 7, 0],
] as const;

const HATS = ['x-o-x-o-x-o-x-o-', 'x-x-x-x-x-x-x-xx', 'x-oxx-o-x-oxx-ox', 'xoxoxoxoxoxoxoxo'] as const;

type Chop = readonly [step: number, vowel: 'chopA' | 'chopO', len: number, tone: number];

const CHOPS: readonly (readonly (readonly Chop[])[])[] = [
  [[[6, 'chopA', 1, 1], [14, 'chopO', 1.5, 2]], [[3, 'chopO', 1, 1], [6, 'chopA', 2, 2]]],
  [[[2, 'chopO', 1, 0], [10, 'chopO', 1, 1], [14, 'chopA', 2, 2]], [[6, 'chopA', 1.5, 1]]],
  [[[7, 'chopA', 1, 2], [15, 'chopA', 1, 1]], [[3, 'chopA', 1, 2], [11, 'chopO', 1, 1], [14, 'chopO', 2, 0]]],
];

const RIFF: Note[][] = [
  [[0, 'G4', 1], [3, 'Bb4', 1], [6, 'D5', 1], [8, 'C5', 1], [10, 'Bb4', 1], [12, 'G4', 2]],
  [[0, 'G4', 1], [3, 'Bb4', 1], [6, 'C5', 1], [8, 'D5', 2], [11, 'F5', 1], [12, 'D5', 2]],
  [[0, 'Eb5', 1], [3, 'D5', 1], [6, 'C5', 1], [8, 'Bb4', 2], [10, 'G4', 1], [12, 'Bb4', 2]],
  [[0, 'D5', 1], [3, 'C5', 1], [6, 'A4', 1], [8, 'F#4', 2], [10, 'A4', 1], [12, 'D5', 2], [14, 'C5', 1]],
];

const KEYS = [0, 2, -2, 1, 3, -1] as const;
const MINOR = [7, 9, 10, 0, 2, 3, 5] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'drop', bars: 8, min: 8, rank: 1 },
    { name: 'verse', bars: 8, min: 8, rank: 4 },
    { name: 'break', bars: 4, min: 4, rank: 3, with: 'drop2' },
    { name: 'drop2', bars: 16, min: 8, rank: 2 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(FUNK, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const groove = ch.pick(GROOVES);
  const bass = ch.pick(BASS);
  const hats = ch.orig ? [...HATS] : shuffle(ch.rng, HATS);
  const chops = ch.pick(CHOPS);
  const harmonies = prog.map((c) => [{ at: 0, tones: pcs(c.pad) }]);
  const riffRows = ch.orig ? RIFF : phrase(ch.rng, RIFF.map(cellsOf), harmonies, MINOR, [65, 77], 67);
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    const m = midi(tr(n));
    return noteName(m < 29 ? m + 12 : m > 40 ? m - 12 : m);
  };
  const tonic = low(prog[0]!.root);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 52, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const dropAt = find(form, 'drop')!;
  const drop2At = find(form, 'drop2');
  const brk = find(form, 'break');
  const rng = createRng(`funk-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const autos: Score['autos'] = [
    { v: 'padLp', points: [[g.at(0), 450], [g.at(dropAt), 1300]] },
    { v: 'riffLp', points: [[g.at(0), 900], [g.at(dropAt), 3200]] },
  ];
  if (brk !== undefined && drop2At !== undefined) {
    autos.push({ v: 'riffLp', points: [[g.at(brk), 3200], [g.at(brk) + s, 700], [g.at(drop2At) - s, 2600], [g.at(drop2At), 4200]] });
  }

  const pattern = (v: string, bar: number, row: string, scale: number) => {
    for (const h of hits(row)) hit(v, bar, h.step, human(h.vel * scale));
  };

  const play808 = (bar: number, row: string, root: string, glideTo?: string) => {
    const steps = hits(row).map((h) => h.step);
    steps.forEach((step, i) => {
      const next = steps[i + 1] ?? 16;
      const note = low(transpose(root, bass[i % bass.length]!));
      const ev: Ev = { v: 'b808', t: g.at(bar, step), bar, step, vel: 1, dur: Math.max(s * 0.6, (next - step) * s - 0.12), note };
      if (glideTo !== undefined && i === steps.length - 1) ev.glide = { to: glideTo, at: (next - step) * s * 0.4, time: s * 1.2 };
      events.push(ev);
    });
  };

  for (const b of g.bars()) {
    const loop = cycle(b.bar - intro, 4);
    const ab = loop % 2;
    const c = prog[loop]!;
    const nextRoot = low(prog[cycle(loop + 1, 4)]!.root);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const verse = b.name === 'verse';
    const fill = b.last && (b.name === 'drop' || verse) && b.bar + 1 < outro;

    if (b.name === 'intro') {
      hit('kick', b.bar, 0, human(0.86));
      hit('tom', b.bar, 8, human(0.9));
      pattern('slap', b.bar, groove.slap[ab]!, 0.8);
      pattern('shaker', b.bar, '-o-o-o-o-o-o-o-o', 1);
      if (b.index >= b.bars - 2) pattern('clap', b.bar, groove.clap[ab]!, 0.82);
      if (b.index >= 1) pattern('hat', b.bar, hats[0]!, 0.7);
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.6);
      if (b.last) for (const [step, v, len, tone] of chops[1]!) play(v, b.bar, step, tr(c.stab[tone]!), len * s, human(0.85));
    }

    if (drop || verse) {
      pattern('kick', b.bar, groove.kick[ab]!, verse ? 0.9 : 1);
      pattern('tom', b.bar, [...groove.tom[ab]!].map((ch, i) => (groove.kick[ab]![i] === '.' ? ch : '.')).join(''), 1);
      pattern('slap', b.bar, groove.slap[ab]!, 1);
      pattern('clap', b.bar, groove.clap[ab]!, 1);
      hit('snare', b.bar, 4, human(0.7));
      hit('snare', b.bar, 12, human(0.7));
      pattern('shaker', b.bar, '-o-o-o-o-o-o-o-o', 1);
      pattern('hat', b.bar, hats[verse ? 1 : (loop + (b.name === 'drop2' ? 2 : 0)) % hats.length]!, verse ? 0.75 : 1);
      if (ab === 1 && e >= 0.5) for (let i = 0; i < 3; i++) hit('hat', b.bar, 14 + (2 * i) / 3, 0.5 + 0.15 * i);
      if (loop === 3 && e >= 0.4) hit('hatO', b.bar, 10, 0.7);
      if (drop) play808(b.bar, groove.kick[ab]!, c.root, loop === 3 && e >= 0.6 ? nextRoot : undefined);
      else play('b808', b.bar, 0, low(c.root), s * 2.6, 0.9);
      const chopRow = chops[verse ? ab : (ab + 1) % 2]!;
      if (verse || ab === 1 || e >= 0.8) for (const [step, v, len, tone] of chopRow) play(v, b.bar, step, tr(c.stab[tone]!), len * s, human(0.9));
      if (verse) chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.55);
    }

    if (drop) {
      if (b.index % 4 === 0 && (b.index === 0 || e >= 0.6)) chord('orch', b.bar, 0, c.stab.map(tr), s * 3, 1);
      if (loop === 3 && e >= 0.65) {
        chord('orch', b.bar, 12, c.stab.map(tr), s * 1.5, 0.85);
        chord('orch', b.bar, 14, c.stab.map(tr), s * 1.5, 0.95);
      }
      const riffOn = b.name === 'drop2' ? e >= 0.5 : e >= 0.85 && b.index >= b.bars / 2;
      if (riffOn) for (const [step, note, len, vel] of riffRows[loop]!) play('riff', b.bar, step, tr(note), len * s * 0.9, human(vel ?? 0.9));
      if (b.name === 'drop2' && loop === 3 && e >= 0.7) {
        play('apito', b.bar, 12, 'D7', s * 1.2, 0.9);
        play('apito', b.bar, 14, 'D7', s * 1.8, 1);
      }
    }

    if (fill) {
      for (const step of [12, 13, 14, 15]) hit('tom', b.bar, step, human(0.55 + (step - 12) * 0.1));
    }

    if (b.name === 'break') {
      if (b.index === 0) {
        play('b808', b.bar, 0, low(c.root), g.barLength * 0.9, 1);
        hit('kick', b.bar, 0, 1);
        chord('orch', b.bar, 0, c.stab.map(tr), s * 4, 1);
        events.push({ v: 'siren', t: g.at(b.bar, 4), bar: b.bar, step: 4, vel: 0.8, end: g.at(b.bar + Math.max(1, b.bars - 1), 0) });
      }
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.7);
      pattern('hat', b.bar, b.index >= b.bars - 2 ? 'xoxoxoxoxoxoxoxo' : 'x...x...x...x...', 0.7);
      pattern('slap', b.bar, groove.slap[ab]!, 0.7);
      if (b.last) for (const step of [0, 4, 8, 10, 12, 13, 14, 15]) hit('clap', b.bar, step, 0.45 + (step / 15) * 0.5);
      else hit('tom', b.bar, 8, human(0.85));
    }

    if (b.name === 'outro') {
      if (b.index < b.bars - 1) {
        hit('kick', b.bar, 0, human(0.9));
        hit('tom', b.bar, 8, human(0.85));
        pattern('clap', b.bar, groove.clap[0], 0.85);
        pattern('slap', b.bar, groove.slap[ab]!, 0.8);
        pattern('hat', b.bar, hats[0]!, 0.6);
        chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.55);
        if (b.index === 0) play('b808', b.bar, 0, low(c.root), s * 3, 1);
      } else {
        play('b808', b.bar, 0, tonic, g.barLength * 1.2, 1);
        hit('kick', b.bar, 0, 1);
        chord('orch', b.bar, 0, prog[0]!.stab.map(tr), s * 4, 1);
        hit('crash', b.bar, 0, 0.8);
      }
    }
  }

  const riseFrom = Math.max(0, dropAt - 2);
  if (dropAt > 0) events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(dropAt) - s * 2, bar: riseFrom, step: 0, vel: 1 });
  if (brk !== undefined && drop2At !== undefined) {
    const from = Math.max(brk, drop2At - 2);
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2At) - s * 2, bar: from, step: 0, vel: 1 });
  }
  for (const bar of [dropAt, drop2At]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
    if (bar === dropAt) play('apito', bar, 0, 'D7', s * 3, 1);
  }
  const loudest = new Map<string, Ev>();
  for (const e of events) {
    const k = `${e.v}@${e.t}`;
    const o = loudest.get(k);
    if (o === undefined || e.vel > o.vel) loudest.set(k, e);
  }

  return {
    id: trackId(FUNK, seed, p),
    template: FUNK.id,
    seed,
    params: p,
    title: ch.orig ? FUNK.ref.title : titleOf(seed),
    genre: FUNK.genre,
    bpm: p.bpm,
    swing: 52,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 2.2, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.25, drive: 1.6, softness: 1.5, pumpDepth: 0.16, pumpRelease: 0.2 },
    events: [...loudest.values()],
    autos,
    accents: { kick: 'kick', tom: 'kick', clap: 'snare', chopA: 'stab', chopO: 'stab', orch: 'stab', riff: 'melody', impact: 'drop' },
  };
}

export const FUNK: Template = {
  id: 'funk',
  genre: 'funk carioca tamborzão',
  bpm: [124, 140],
  ref: { seed: 'laje-de-cima', title: 'Laje de Cima', params: { bpm: 130, bars: 48, energy: 0.8 } },
  score,
};
